import { resoudreElementsCadre } from '@taki/data-model/cadre-canvas';
import { useCadreSurface } from '@taki/ui/cadre-canvas';
import { jourDansFuseau } from '@taki/data-model';
import type { ContexteDeCondition } from '@taki/data-model';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import {
  BROUILLON_ID,
  CanvasBoundary,
  PhotosDeFiches,
  ScreenLayout,
  ViewHost,
  projectRecord,
  usePendingAppAction,
  usePersonnesPort,
  usePref,
  useScreen,
} from '@taki/ui';
import type { ContexteDAction, ScreenDoor, ScreenQuery } from '@taki/ui';
import type { FamilleRepository } from '@taki/sdk';
import type { AppDeclaree, FicheGenerique } from './app-declaree.js';
import { assemblerEcran } from './assemblage.js';
import type { EcranAssemble, EnvironnementDesEffets } from './assemblage.js';
import type { AvisDEffet } from './effets-socle.js';
import { IconeDeFiche } from './icone.js';
import { jeuxDesInstances, resoudreDeclaration } from './resolution.js';
import { useAppRuntime } from './runtime.js';
import { AiHistoryPanel } from './AiHistoryPanel.js';
import { contenuDeCarteFichiers } from './CarteDesFichiers.js';
import { useGestesDeFiche } from './gestes-de-fiche.js';
import { chatterActifSurLeJeu, useChatterDuGabarit } from './chatter.js';
import type { AncreDuChatter } from './chatter.js';
import { politiqueDeFichiers } from './fichiers.js';
import { latestLoad } from './latestLoad.js';
import { useFournisseurDePhotos } from './photos.js';

/**
 * L'ECRAN GENERIQUE DU SOCLE : il INTERPRETE une declaration, il ne la
 * recompile pas.
 *
 * CE QU'IL REMPLACE. Chaque app avait son `<Id>Screen.tsx` : cinq cents lignes
 * dont quatre cent quatre-vingts etaient les memes d'une app a l'autre (charger
 * chaque jeu avec son type, compter le jeu complet de chaque porte, poser les
 * bindings, raboter les filtres et les stats a la preference du poste, tenir la
 * puce de sortie, passer vingt props au gabarit). Ce qui differait tenait dans
 * deux fichiers de donnees et un registre de fonctions. Ce composant est ce
 * qu'il restait de commun, ecrit une fois.
 *
 * CE QU'IL NE FAIT PAS, ET NE FERA JAMAIS. Il n'evalue aucune expression, ne lit
 * aucune condition, ne construit aucune chaine a partir d'un gabarit : c'est la
 * ligne rouge de `packages/ui/src/views/screens.ts`, et elle vaut ici aussi.
 * Tout comportement vient soit d'un MECANISME DU SOCLE que le fichier NOMME,
 * soit d'une FONCTION DE L'APP que le fichier NOMME.
 *
 * LA STRUCTURE EST CELLE QUE LA CAGE IMPOSE (`docs/GABARIT-ECRAN.md`) :
 * - UN seul `ScreenLayout` a la racine, et RIEN a cote (regle G6) : la CREATION
 *   est une FICHE VIDE en zone 5, tenue par le socle ; l'EDITION se fait EN
 *   PLACE sur la fiche, enregistrement automatique ;
 * - zone 5 pilotee par les VUES du socle : aucune liste, aucun tableau, aucun
 *   kanban dessine a la main ;
 * - les etats standard vont au `ViewHost` ;
 * - acces data UNIQUEMENT via `@taki/sdk`, jamais depuis une vue.
 *
 * Les compteurs du menu se calculent sur le jeu COMPLET de chaque porte, jamais
 * sur la porte courante (G15).
 */

/* ------------------------------------------------------------------ */
/* Le chargement : un appel par JEU, pas par porte                     */
/* ------------------------------------------------------------------ */

/**
 * LE MODE ARCHIVES voyage dans la requete et c'est l'ECRAN qui le traduit en
 * option de SDK. On relaie `'all'`, JAMAIS `'only'`, et c'est un choix : cet
 * ecran est en pagination CLIENTE, donc tous ses chiffres se calculent sur ce
 * qu'il a charge, compteurs du menu compris. Avec `'only'` le jeu actif
 * quitterait la memoire et les compteurs tomberaient a zero le temps du mode, ce
 * que la regle G15 interdit. Avec `'all'` le moteur fait le partage : les
 * archives a l'ecran, les actifs dans les compteurs et les stats.
 *
 * Le cadrage par `orgId` est pose par le SDK, jamais par l'ecran.
 */
const modeDe = (query: ScreenQuery) =>
  ({ archived: query.archivesMode ? 'all' : 'exclude' }) as const;

/** Le `type` d'une fiche, lu sans rien supposer de son jeu. */
const typeDeFiche = (fiche: FicheGenerique): string =>
  typeof fiche['type'] === 'string' ? fiche['type'] : '';

/**
 * CHAQUE JEU INTERROGE AVEC SON TYPE, et ce n'est pas une optimisation : les
 * rules ne peuvent lire le type d'un document que si la REQUETE le fixe. Un
 * membre dont l'acces a une famille est regle par type se verrait refuser la
 * liste entiere sans ce filtre (`docs/ACCES.md`). Le `filter` qui suit est la
 * seconde moitie de la meme prudence : une famille est PARTAGEE par toutes les
 * apps de l'org, et un document ecrit avant le champ `type` remonte sans lui.
 */
async function chargerUnJeu(
  depot: FamilleRepository,
  type: string | undefined,
  query: ScreenQuery,
): Promise<FicheGenerique[]> {
  const fiches = await depot.list({
    ...(type === undefined ? {} : { type }),
    ...modeDe(query),
  });
  return type === undefined ? fiches : fiches.filter((fiche) => typeDeFiche(fiche) === type);
}

/**
 * TOUS LES JEUX SE CHARGENT ENSEMBLE, A CHAQUE PASSAGE.
 *
 * POURQUOI TOUS, ALORS QU'UNE SEULE PORTE EST A L'ECRAN : les COMPTEURS du menu
 * portent sur le jeu COMPLET de chaque porte et restent GELES (regle G15) ; ne
 * charger que la porte active ferait tomber les autres a zero des qu'on arrive
 * directement sur l'une d'elles (lien `?s=...`).
 */
async function chargerLesJeux(
  assemble: EcranAssemble,
  query: ScreenQuery,
): Promise<Map<string, FicheGenerique[]>> {
  const charges = await Promise.all(
    assemble.jeux.map(
      async ({ clef, jeu, depot }) => [clef, await chargerUnJeu(depot, jeu.type, query)] as const,
    ),
  );
  return new Map(charges);
}

/**
 * Ce qu'une porte montre, une fois les jeux charges : le jeu qu'elle declare,
 * limite a sa TRANCHE. C'est ici, et seulement ici, que la tranche s'applique :
 * compteur, vues, recherche et stats ne voient donc que ses fiches.
 */
function fichesDePorte(
  assemble: EcranAssemble,
  porteId: string,
  parJeu: Map<string, FicheGenerique[]>,
  contexte: ContexteDeCondition,
): FicheGenerique[] {
  const clef = assemble.clefDeJeuDePorte(porteId);
  const fiches = clef === null ? [] : (parJeu.get(clef) ?? []);
  const tranche = assemble.tranche(porteId, contexte);
  return tranche === null ? fiches : fiches.filter(tranche);
}

/**
 * CE QUE DEVIENNENT LES JEUX CHARGES : les compteurs du menu et les fiches des
 * portes canvas. UNE seule fonction, appelee par le chargement de l'ecran ET par
 * le recompte silencieux (cf. `EcranDeclare`) : deux calculs ecrits a cote
 * finiraient par ne plus dire la meme chose.
 *
 * LE COMPTEUR DIT LA MEME VERITE QUE LA ZONE 5 HORS MODE ARCHIVES : les archives
 * en sont exclues, dans les DEUX modes.
 */
function compterLesPortes(
  assemble: EcranAssemble,
  portesCanvas: Readonly<Record<string, unknown>>,
  parJeu: Map<string, FicheGenerique[]>,
  contexte: ContexteDeCondition,
): { comptes: Record<string, number>; canvas: Record<string, FicheGenerique[]> } {
  const comptes: Record<string, number> = {};
  const canvas: Record<string, FicheGenerique[]> = {};
  for (const porte of assemble.portes) {
    const fiches = fichesDePorte(assemble, porte.id, parJeu, contexte);
    comptes[porte.id] = fiches.filter((fiche) => fiche['archivedAt'] == null).length;
    if (portesCanvas[porte.id] !== undefined) canvas[porte.id] = fiches;
  }
  return { comptes, canvas };
}

/** Les fiches ACTIVES d'une liste : la verite du compteur du menu. */
const fichesActives = (fiches: readonly FicheGenerique[]): FicheGenerique[] =>
  fiches.filter((fiche) => fiche['archivedAt'] == null);

/* ------------------------------------------------------------------ */
/* L'ecran                                                             */
/* ------------------------------------------------------------------ */

/** Ce que le chassis rend de statistiques dans l'en-tete (zone 2c) : trois. */
const STATS_MAX = 3;

/**
 * L'ANTI-REBOND du recompte silencieux, en ms : une rafale d'ecritures (une
 * surface qui enregistre champ par champ, un import) ne fait qu'UNE relecture.
 */
const DELAI_DU_RECOMPTE = 150;

/** Combien de temps l'avis d'un effet du socle reste sur son bouton, en ms. */
const DUREE_DE_L_AVIS = 5000;

export function EcranDeclare({ app }: { app: AppDeclaree }): JSX.Element {
  const { sdk, instances, orgId } = useAppRuntime();
  const [jourCourant, poserJour] = useState(() => jourDansFuseau(new Date(), sdk.timezone));
  useEffect(() => {
    const actualiser = () => poserJour(jourDansFuseau(new Date(), sdk.timezone));
    const timer = setInterval(actualiser, 30_000);
    document.addEventListener('visibilitychange', actualiser);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', actualiser); };
  }, [sdk.timezone]);

  /*
   * LA DECLARATION EFFECTIVE : celle de l'org quand elle existe et se lit,
   * sinon celle du depot. Une instance illisible ne fait pas tomber l'ecran ;
   * elle part au journal, et le panneau de reglages la montre en clair.
   */
  const resolution = useMemo(
    () => resoudreDeclaration(app, instances?.[app.id], jeuxDesInstances(instances)),
    [app, instances],
  );
  useEffect(() => {
    if (resolution.raison === undefined) return;
    console.warn(
      `[taki/app-runtime] App "${app.id}" : la declaration de l'org n'a pas pu etre lue, ` +
        `celle du depot est montee. ${resolution.raison}`,
    );
  }, [app.id, resolution.raison]);

  /*
   * L'ASSEMBLAGE, fait UNE fois pour ce SDK et cette declaration : les bindings
   * recrees a chaque rendu relanceraient la projection a chaque frappe dans la
   * recherche.
   */
  /*
   * CE QUE LES EFFETS D'ACTION LISENT AU CLIC : la fiche ouverte, la selection,
   * la relecture de l'ecran. Des REFS, tenues a jour a chaque rendu plus bas :
   * l'assemblage est memoise, et le reconstruire a chaque ouverture de fiche
   * relancerait la projection. `environnement` est donc STABLE.
   *
   * L'AVIS D'UN EFFET DU SOCLE (« 3 fiches créées ») se rend SUR SON BOUTON, le
   * temps de le lire (`DUREE_DE_L_AVIS`), comme l'accuse du bouton « Copier » :
   * la rangee 3a n'a pas d'indicateur a elle, et une fenetre de plus a refermer
   * pour un geste reussi serait de trop.
   */
  const contexteRef = useRef<{ ficheId: string | null; selection: readonly string[] }>({
    ficheId: null,
    selection: [],
  });
  const rechargerRef = useRef<() => void>(() => {});
  /* LES JEUX CHARGES, tenus a jour AVANT que l'ecran projette ses lignes : les
     totaux lies par ligne les groupent (`totaux-lies.ts`). */
  const jeuxChargesRef = useRef<Map<string, FicheGenerique[]>>(new Map());
  const [avis, setAvis] = useState<{ porteId: string; effet: string; avis: AvisDEffet } | null>(
    null,
  );
  useEffect(() => {
    if (avis === null || avis.avis.etat === 'enCours') return;
    const minuteur = window.setTimeout(() => setAvis(null), DUREE_DE_L_AVIS);
    return () => window.clearTimeout(minuteur);
  }, [avis]);
  const environnement = useMemo<EnvironnementDesEffets>(
    () => ({
      lireContexte: (porteId: string): ContexteDAction => ({ porteId, ...contexteRef.current }),
      orgId: orgId ?? '',
      recharger: () => rechargerRef.current(),
      annoncer: (porteId, effet, avisRecu) => setAvis({ porteId, effet, avis: avisRecu }),
      lireJeu: (clef) => jeuxChargesRef.current.get(clef),
    }),
    [orgId],
  );

  /* LA PERSONNE CONNECTEE, pour les filtres « est moi » : lue au rendu, elle
     arrive apres le montage, et les filtres se recalculent avec elle. */
  const personnes = usePersonnesPort();
  const moiPersonId = personnes?.moi?.personId ?? null;
  const moiUid = personnes?.moi?.userId ?? null;
  const contexteDesFiltres = useMemo(
    () => ({ moi: { personId: moiPersonId, uid: moiUid } }),
    [moiPersonId, moiUid],
  );

  const assemble = useMemo(
    () => assemblerEcran(resolution.declaration, sdk, app.fonctions, app.effets, environnement),
    [resolution.declaration, sdk, app.fonctions, app.effets, environnement, jourCourant],
  );

  /*
   * LES COMPTEURS DU MENU, memorises a chaque chargement : ils portent sur le
   * jeu COMPLET de chaque porte, jamais sur la porte courante (G15). Un seul
   * etat pour toutes les portes : un `useState` par porte dirait le meme fait
   * plusieurs fois, et finirait par ne plus dire la meme chose.
   */
  const [comptes, setComptes] = useState<Readonly<Record<string, number>>>({});
  const countLoad = useRef(latestLoad());
  /* LA DERNIERE REQUETE DU CHARGEUR, et ce que le recompte silencieux relit a
     chaque passage : il rejoue le meme mode (archives ou non) que l'ecran. */
  const derniereQuery = useRef<ScreenQuery | null>(null);

  /* LES FICHES DE LA PORTE CANVAS, gardees a part : sa zone 5 appartient a
     l'app (G23), et sa surface les dessine. Vide tant qu'aucune porte n'en
     declare, ce qui est le cas ordinaire. */
  const [fichesDeCanvas, setFichesDeCanvas] = useState<Readonly<Record<string, FicheGenerique[]>>>(
    {},
  );

  /*
   * CE QUE CHAQUE PORTE AJOUTE A SA DECLARATION.
   *
   * TOUT CE QUI EST UN MOT OU UN NOM VIENT DE `taki.screens.json` ; ce qui reste
   * ici est ce qu'aucun fichier de donnees ne peut porter : l'entree a froid et
   * les gestes de fiche (l'assemblage les a cables sur le SDK), et la preference
   * du POSTE.
   *
   * PAS DE `load` PAR PORTE, ET C'EST VOULU : le chargeur de l'ECRAN charge tous
   * les jeux et rend la tranche de la porte demandee. Une porte qui ne
   * chargerait qu'elle priverait les autres de leur compteur.
   */
  const doors: ScreenDoor<FicheGenerique>[] = assemble.portes.map((porte) => {
    const depot = assemble.depotDePorte(porte.id);
    const jeu = assemble.jeuDePorte(porte.id);
    const declarees = assemble.stats(porte.id);
    const estCanvas = app.canvas[porte.id] !== undefined;
    return {
      ...porte,
      ...(depot === null
        ? {}
        : {
            /* L'ENTREE A FROID LIT DANS LE BON JEU : un id venu d'ailleurs (le
               clic sur une ligne de carte liee) appartient au jeu de la porte
               LIEE, jamais a celui de l'ecran. */
            get: async (id: string) => {
              const fiche = await depot.get(id);
              if (fiche === null) return null;
              const type = jeu?.type;
              return type === undefined || typeDeFiche(fiche) === type ? fiche : null;
            },
            ...assemble.gestes(porte.id),
          }),
      bindings: assemble.bindings(porte.id),
      filters: assemble.filtres(porte.id, contexteDesFiltres),
      presets: assemble.favoris(porte.id),
      /* Les agregats declares, calcules sur le jeu VIVANT : les trois premiers
         dans l'ordre de la declaration (le Studio choisit et ordonne).
         SUR UNE PORTE CANVAS, le jeu vivant est celui du dernier chargement, que
         ses ecritures directes ne rechargent pas : les stats se calculent donc
         sur les fiches de la porte que le recompte silencieux tient a jour,
         archives exclues (la meme verite que le compteur du menu). */
      stats: (live: FicheGenerique[]) =>
        declarees(estCanvas ? fichesActives(fichesDeCanvas[porte.id] ?? []) : live).slice(0, STATS_MAX),
      actions: assemble.actions(porte.id).map((action) => {
        /* L'AVIS PREND LA PLACE DU LIBELLE, sur le seul bouton qui l'a
           declenche : meme porte, meme effet. En cours ou en echec, le bouton
           est eteint, et son survol dit la phrase entiere (G16). */
        const fn = assemble.ecran.portes
          .find((declaree) => declaree.id === porte.id)
          ?.actions?.find((declaree) => declaree.id === action.id)?.fn;
        if (avis === null || avis.porteId !== porte.id || avis.effet !== fn) return action;
        const eteint = avis.avis.etat !== 'fait';
        const raison = avis.avis.detail ?? avis.avis.texte;
        return {
          ...action,
          label: avis.avis.texte,
          ...(eteint ? { disabled: true, disabledReason: raison } : {}),
        };
      }),
    };
  });

  const screen = useScreen<FicheGenerique>({
    appId: app.id,
    screenId: assemble.ecran.id,
    /* LE CHARGEUR EST PARTAGE PAR TOUTES LES PORTES : elles se distinguent par
       `query.section`, et c'est le moteur qui le rappelle au changement de
       porte. Il charge chaque jeu une fois, memorise les compteurs, puis rend
       la tranche demandee. */
    load: async (query) => {
      derniereQuery.current = query;
      const commitIfLatest = countLoad.current();
      const parJeu = await chargerLesJeux(assemble, query);
      jeuxChargesRef.current = parJeu;
      const { comptes: frais, canvas } = compterLesPortes(assemble, app.canvas, parJeu, contexteDesFiltres);
      commitIfLatest(() => {
        setComptes(frais);
        setFichesDeCanvas(canvas);
      });
      return fichesDePorte(assemble, query.section ?? assemble.porteArrivee, parJeu, contexteDesFiltres);
    },
    getId: (record) => String(record['id'] ?? ''),
    types: assemble.types,
    bindings: assemble.bindings(assemble.porteArrivee),
    doors,
    // La vue et la porte d'arrivee sont DECLAREES : le schema du fichier refuse
    // une vue d'arrivee absente du catalogue monte.
    defaultView: assemble.vueArrivee,
    /* LA PILE D'ARRIVEE VIENT DU FICHIER, comme le catalogue de tri. Vide tant
       que rien ne la declare : le jeu arrive dans l'ordre du chargeur. */
    defaultSort: assemble.tri.defaut,
    defaultSection: assemble.porteArrivee,
  });
  /* LES LECTURES DES EFFETS, a jour a chaque rendu. Un brouillon, un modele ou
     un enfant de modele ne sont pas des fiches : l'effet ne recoit alors aucune
     fiche, plutot qu'un id qu'aucun repository ne connait. */
  const ficheOuverte =
    screen.openId == null ||
    screen.openId === BROUILLON_ID ||
    screen.openId.includes('/') ||
    screen.modelesMode
      ? null
      : screen.openId;
  contexteRef.current = { ficheId: ficheOuverte, selection: screen.selection };

  /*
   * LE RECOMPTE SILENCIEUX D'UNE PORTE CANVAS.
   *
   * POURQUOI. Sur une porte NATIVE, chaque geste (creation, archivage,
   * restauration, suppression) rappelle `reload()` : le chargeur repasse et les
   * compteurs, les stats et les fiches suivent. Sur une porte CANVAS (G23), la
   * surface de l'app ecrit DIRECTEMENT par le SDK : personne ne relance le
   * chargeur, et la pastille du menu comme les stats de l'en-tete resteraient
   * figees jusqu'au rechargement de la page. Le SDK dit chaque ecriture reussie
   * (`sdk.ecritures`) ; l'ecran n'a plus qu'a recompter.
   *
   * SEULEMENT SUR UNE PORTE CANVAS : ailleurs les gestes rechargent deja, un
   * recompte de plus serait une double lecture. Et JAMAIS PAR `screen.reload()` :
   * il allumerait l'etat de chargement du gabarit et demonterait la surface, qui
   * perdrait son etat en pleine saisie. On relit les jeux a cote, et on ne
   * touche que ce qui est compte (menu, stats, fiches de la surface).
   *
   * PAS DE MENSONGE SUR UN ECHEC : l'ecriture a reussi, on le dit en console et
   * c'est le prochain chargement qui fera foi. Le jeton `latestLoad` laisse le
   * dernier arrive gagner : un chargement ou un recompte plus recent, lance
   * pendant la lecture, rend celle-ci caduque.
   */
  const assembleRef = useRef(assemble);
  assembleRef.current = assemble;
  const contexteDesFiltresRef = useRef(contexteDesFiltres);
  contexteDesFiltresRef.current = contexteDesFiltres;
  const porteActiveRef = useRef(screen.door.id);
  porteActiveRef.current = screen.door.id;
  useEffect(() => {
    /* Un faux SDK de test ou un SDK plus ancien peut n'avoir pas de signal : */
    if (sdk.ecritures === undefined) return;
    const familles = new Set(assemble.jeux.map(({ jeu }) => jeu.family));
    let minuteur: number | undefined;
    let vivant = true;
    const recompter = async () => {
      const query = derniereQuery.current;
      if (query === null) return;
      const commitIfLatest = countLoad.current();
      try {
        const parJeu = await chargerLesJeux(assembleRef.current, query);
        const { comptes: frais, canvas } = compterLesPortes(
          assembleRef.current,
          app.canvas,
          parJeu,
          contexteDesFiltresRef.current,
        );
        commitIfLatest(() => {
          if (!vivant) return;
          jeuxChargesRef.current = parJeu;
          setComptes(frais);
          setFichesDeCanvas(canvas);
        });
      } catch (erreur) {
        console.warn('[taki/app-runtime] Le recompte apres ecriture a echoue, le prochain chargement fera foi.', erreur);
      }
    };
    const arreter = sdk.ecritures.observer((ecriture) => {
      if (!familles.has(ecriture.famille)) return;
      if (app.canvas[porteActiveRef.current] === undefined) return;
      /* Un recompte deja en vol lit des donnees d'AVANT cette ecriture : on le
         rend caduc tout de suite, le suivant partira apres l'anti-rebond. */
      countLoad.current();
      window.clearTimeout(minuteur);
      minuteur = window.setTimeout(() => {
        minuteur = undefined;
        void recompter();
      }, DELAI_DU_RECOMPTE);
    });
    return () => {
      vivant = false;
      arreter();
      window.clearTimeout(minuteur);
    };
  }, [sdk, assemble, app.canvas]);

  /* LA CAPACITE FICHIERS DU JEU DE LA PORTE, sur la declaration EFFECTIVE. */
  const jeuAffiche = assemble.jeuDePorte(screen.door.id);
  const politique = politiqueDeFichiers(resolution.declaration.champs, jeuAffiche);
  /*
   * LE CONTENU DE LA CARTE DES FICHIERS, pose par la fiche a son rang. `null` :
   * le jeu ne les ouvre pas, la carte n'apparait pas, et pas davantage sur un
   * modele ou un enfant de modele (aucun fichier ne s'y joint). Sur un
   * BROUILLON, pas encore en base, la fiche rend son apercu a la place de ce
   * contenu : il suffit de ne pas dire `null`.
   */
  const fichiers = contenuDeCarteFichiers({
    sdk, jeu: jeuAffiche, politique, ficheOuverte,
    brouillon: screen.openId === BROUILLON_ID && !screen.modelesMode,
  });
  /* LES PHOTOS DE REFERENCE : un fournisseur par ecran (cache par id, URL d'objet
     revoquees au demontage), pose autour du ViewHost. Sans lui, pas de photo. */
  const photos = useFournisseurDePhotos(sdk);
  rechargerRef.current = screen.reload;
  /* UNE TRANCHE « MOI » SE RELIT QUAND LA PERSONNE CONNECTEE ARRIVE : le
     premier chargement part souvent avant elle, et la porte resterait vide. */
  const trancheeSurMoi = assemble.portes.some((porte) => assemble.tranche(porte.id) !== null);
  const moiCharge = useRef(contexteDesFiltres);
  useEffect(() => {
    if (moiCharge.current === contexteDesFiltres) return;
    moiCharge.current = contexteDesFiltres;
    if (trancheeSurMoi) rechargerRef.current();
  }, [contexteDesFiltres, trancheeSurMoi]);

  /* LES GESTES DE FICHE DECLARES (`EFFETS_DE_FICHE`) : dans le menu « ... » de
     la fiche ouverte, a la suite de ceux du moteur. */
  const gestesDeFiche = useGestesDeFiche({
    sdk,
    actions: assemble.ecran.portes.find((porte) => porte.id === screen.door.id)?.actions,
    porte: assemble.ecran.portes.find((porte) => porte.id === screen.door.id)?.label ?? '',
    jeu: jeuAffiche,
    politique,
    ficheOuverte,
    record: ficheOuverte === null ? undefined : screen.records.find((fiche) => String(fiche['id'] ?? '') === ficheOuverte),
    binding: assemble.bindings(screen.door.id).find((binding) => binding.type === 'core.fiche'),
    raisonAcces: screen.raisonAcces,
  });

  /*
   * LE CHATTER DE L'OBJET AFFICHE (prop `chatter` du gabarit, `chatter.ts`) :
   * celui de la FICHE ouverte, sinon celui de la PORTE active. Aucun sur un
   * brouillon, un modele ou un enfant de modele : pas d'objet en base a
   * rattacher. Le titre voyage avec l'ancre pour que le centre nomme l'objet
   * sans le relire (« Ici : Studio Lumiere SARL »).
   */
  const ficheAffichee = ficheOuverte === null
    ? undefined
    : screen.records.find((fiche) => String(fiche['id'] ?? '') === ficheOuverte);
  /* LE TITRE TEL QUE LA FICHE L'AFFICHE (`projectRecord`) : un titre a
     catalogue voyage par son LIBELLE (« Rédaction »), jamais par sa clef, vers
     le centre de messages et les notifications du chatter. */
  const bindingDeFiche = assemble.bindings(screen.door.id).find((binding) => binding.type === 'core.fiche');
  const titreProjete = ficheAffichee !== undefined && ficheOuverte !== null && bindingDeFiche?.roles.title !== undefined
    ? projectRecord(ficheAffichee, ficheOuverte, bindingDeFiche).title
    : '';
  const titreDeLaFiche = titreProjete === ficheOuverte ? '' : titreProjete;
  // VAGUE DU SOIR, R9 : la FICHE seulement (plus de chatter de porte), et
  // seulement si sa base ne l'a pas eteint (`jeux[].chatter: false`).
  const ancreDuChatter: AncreDuChatter | null =
    ficheOuverte !== null && jeuAffiche !== null && chatterActifSurLeJeu(resolution.declaration.champs, jeuAffiche)
      ? { family: jeuAffiche.family, recordId: ficheOuverte, appId: app.id, doorId: screen.door.id, titre: titreDeLaFiche || ficheOuverte }
      : null;
  const chatter = useChatterDuGabarit(sdk, ancreDuChatter);

  /*
   * LE RACCOURCI DU DOCK ECRIT DANS LA FICHE (`fiche.actions`), le seul qui
   * passe ici : il vise l'app sans nommer de porte, et c'est ICI qu'il ouvre la
   * MEME fiche vide que le « + Ajouter » de la porte. Les raccourcis DERIVES
   * des portes (`raccourcis.ts` de `@taki/shell`) nomment leur porte : le shell
   * y passe par l'adresse et c'est le GABARIT qui les consomme, pas cet effet.
   *
   * LA PORTE EST CELLE QUI DECLARE L'AJOUT, et elle doit l'etre : le dock peut
   * lancer l'app depuis n'importe ou, donc la porte active a l'arrivee n'est pas
   * forcement celle qui sait creer. Le moteur y passe d'abord, puis ouvre le
   * brouillon.
   *
   * IL NE VOIT PAS L'INTENTION DU SOCLE : le « + Ajouter » d'une carte liee pose
   * une intention qui nomme une PORTE et n'a pas d'`appId`, donc
   * `usePendingAppAction` la laisse passer et c'est le GABARIT qui la consomme.
   */
  const { actionId: intention, consume: consommerLIntention } = usePendingAppAction(app.id);
  const { ouvrirBrouillon } = screen;
  useEffect(() => {
    if (intention == null) return;
    const porte = assemble.ecran.portes.find((p) =>
      (p.actions ?? []).some((action) => action.id === intention && action.kind === 'add'),
    );
    if (porte !== undefined) ouvrirBrouillon({ porte: porte.id });
    consommerLIntention();
  }, [intention, consommerLIntention, ouvrirBrouillon, assemble]);

  /*
   * LE MENU : les LIBELLES viennent de la declaration (renommer une porte est
   * une ecriture de chaine dans `taki.screens.json`), les COMPTEURS sont ceux du
   * dernier chargement, sur le jeu COMPLET de chaque porte (G15).
   */
  const menuItems = useMemo(
    () =>
      /* UNE PORTE HORS NAVIGATION (`navigation: false`) n'a pas d'entree : on y
         entre par un lien d'une autre porte. */
      assemble.portes
        .filter((porte) => porte.navigation !== false)
        .map((porte) => ({
          id: porte.id,
          label: porte.label,
          count: comptes[porte.id] ?? 0,
        })),
    [assemble, comptes],
  );

  /*
   * Le libelle de la puce de sortie de la rangee 3 : le nom de la vue collection
   * ou la sortie de fiche ramene. L'ecran ne teste AUCUN niveau, c'est le
   * gabarit qui le deduit de `views` + `activeViewId`.
   */
  const exitLabel = screen.views.find((vue) => vue.id === screen.lastCollectionViewId)?.label;

  /*
   * LES ETATS VIDE ET INTROUVABLE VIENNENT DE LA PORTE, et rien n'est ecrit ici
   * quand elle n'en declare pas : le `ViewHost` porte deja les phrases neutres
   * du socle, et les recopier en ferait deux mots pour un etat.
   */
  const porteDeclaree = assemble.ecran.portes.find((porte) => porte.id === screen.door.id);
  const vide = porteDeclaree?.vide;
  const introuvable = porteDeclaree?.introuvable;

  const Canvas = app.canvas[screen.door.id];
  const cadreSurface = useCadreSurface(screen.door.id, screen.door.canvas ?? { level: 'space' }, screen.query.search);
  const { tools: outilsSurface, ...propsSurface } = cadreSurface.props;

  return (
    <ScreenLayout
      /* HORS GABARIT (G23) : quand la porte active est un Space Canvas, le
         moteur le sert deja resolu et l'ecran le passe TEL QUEL. Sur une porte
         ordinaire il vaut `null`, et le gabarit ne change rien. */
      canvas={screen.door.canvas}
      /* LES ETATS STANDARD DE LA ZONE 5, mais SEULEMENT sur une porte canvas :
         ailleurs c'est le `ViewHost` qui les tient, et les passer deux fois est
         interdit (checklist, item 10). Sur un canvas il n'y a pas de ViewHost :
         sans ces props, un chargement rate s'afficherait comme une surface
         vide, sans moyen de relancer. */
      {...(screen.door.canvas != null
        ? {
            loading: screen.status.loading,
            ...(screen.status.error != null
              ? { error: screen.status.error.message, onRetry: screen.reload }
              : {}),
            errorTitle: 'Echec du chargement',
            retryLabel: 'Reessayer',
          }
        : {})}
      /* Zone 1 : identite de l'app (1a) et menu des portes (1b), avec une
         pastille par entree, calculee sur le jeu COMPLET de sa porte. */
      appName={resolution.declaration.fiche.name}
      appIcon={<IconeDeFiche nom={resolution.declaration.fiche.icon} size={22} />}
      menuItems={screen.door.canvas !== null && !resoudreElementsCadre(screen.door.canvas).includes('portes') ? [] : menuItems}
      activeMenuId={screen.chemin?.porteDuMenu ?? screen.section ?? assemble.porteArrivee}
      onMenuSelect={screen.setSection}
      /* Zone 2a : le h1 affiche le label de la porte active ; `title` est le
         repli. Zone 2b : la recherche, avec le placeholder de la porte. Zone
         2c : les stats de la porte, resolues par le moteur ; la porte les a deja
         rabotees au reglage du poste. */
      title={resolution.declaration.fiche.name}
      searchValue={screen.query.search}
      onSearchChange={screen.setSearch}
      searchPlaceholder={screen.door.searchPlaceholder}
      headerStats={screen.door.stats}
      /* Zone 3a : les actions de la porte, puis celles que la vue active
         propose. Zones 3b + 4 : outils, tags et favoris ; le catalogue de tri
         est DECLARE par l'ecran et vaut pour toutes ses portes. */
      quickActions={screen.door.actions}
      viewActions={screen.viewActions}
      tools={Canvas !== undefined ? { ...screen, ...outilsSurface } : screen}
      {...(gestesDeFiche.length === 0
        ? {}
        : { recordMenuActions: [...screen.recordMenuActions, ...gestesDeFiche] })}
      /* Le bouton « Chatter » de la rangee 3 : la fiche ouverte ou la porte,
         rendu par le gabarit, lu par le socle (`chatter.ts`). */
      chatter={chatter}
      sortOptions={assemble.tri.options}
      /* Zone 3c : le selecteur de vues, epingle, JAMAIS conditionnel. Une vue
         sans ce qu'elle exige y est GRISEE avec sa raison, jamais absente. */
      views={screen.views}
      activeViewId={screen.viewId}
      onViewSelect={screen.setView}
      /* Rangee 3 au niveau FICHE : la puce de sortie, l'indicateur
         d'enregistrement avec sa relance, et le curseur de fiche. Les six props
         sont passees SANS condition : le gabarit ne les rend qu'au niveau
         `record`, et une fiche sans elles serait une nasse. */
      onRecordExit={() => screen.openRecord(null)}
      recordExitLabel={exitLabel}
      saveState={screen.saveState}
      onRetrySave={screen.retrySave}
      recordCursor={screen.cursor}
      onRecordNavigate={screen.openRecord}
      /* La zone 5 est PRETEE au ViewHost et aux vues : c'est lui qui tient les
         etats standard et la memoire de defilement par vue. */
      scrollRef={screen.scrollRef}
      {...(Canvas !== undefined ? propsSurface : {})}
    >
      {Canvas !== undefined ? (
        /* La zone 5 appartient a l'app, et le filet devient explicite :
           `CanvasBoundary` est au canvas ce que le ViewHost est a une vue, a
           ceci pres qu'une surface custom n'a AUCUNE vue de repli. */
        <CanvasBoundary surfaceId={screen.door.id}>
          <Canvas
            fiches={fichesDeCanvas[screen.door.id] ?? []}
            {...cadreSurface.api}
            porte={screen.door.id}
          />
        </CanvasBoundary>
      ) : (
        /* Zone 5 : le ViewHost tient les etats standard dans la meme priorite
           que le gabarit (erreur > chargement > fiche introuvable > vide >
           contenu), la memoire de defilement par vue et le filet anti-plantage.
           L'etat vide suit la porte, et il ne connait pas la creation : la fiche
           vide PREND la zone 5 (niveau `record`), donc elle ne cohabite jamais
           avec l'invitation a ajouter. */
        <PhotosDeFiches.Provider value={photos}>
          <ViewHost
            state={screen}
            recordFooter={ficheOuverte !== null && jeuAffiche !== null
              ? <AiHistoryPanel sdk={sdk} family={jeuAffiche.family} recordId={ficheOuverte} />
              : null}
            fichiers={fichiers}
            {...(jeuAffiche === null ? {} : { collection: jeuAffiche.family })}
            errorTitle="Echec du chargement"
            retryLabel="Reessayer"
            {...(vide === undefined ? {} : { emptyTitle: vide.titre })}
            {...(vide?.indication === undefined ? {} : { emptyHint: vide.indication })}
            {...(introuvable === undefined ? {} : { notFoundTitle: introuvable.titre })}
            {...(introuvable?.indication === undefined
              ? {}
              : { notFoundHint: introuvable.indication })}
          />
        </PhotosDeFiches.Provider>
      )}
    </ScreenLayout>
  );
}

export default EcranDeclare;
